import { Injectable, signal } from '@angular/core';

export interface IrdIconDefinition {
  readonly name: string;
  /** Full SVG string, e.g. `<svg viewBox="0 0 24 24">...</svg>` */
  readonly data: string;
}

/**
 * Lightweight icon registry for `@ird/ng-headless`.
 *
 * Stores SVG strings by name so `IrdIconComponent` can render
 * icons via `[name]` without inline SVG content projection.
 *
 * No dependency on Angular Material or DomSanitizer.
 */
@Injectable({ providedIn: 'root' })
export class IrdIconRegistryService {
  private readonly _icons = new Map<string, string>();

  /** Bumped on every registration so consumers can react via `computed()`. */
  private readonly _version = signal(0);

  /** Read-only version signal for reactive icon lookups. */
  readonly version = this._version.asReadonly();

  /** Register a single icon. Overwrites any existing icon with the same name. No-op for a nullish icon. */
  register(icon: IrdIconDefinition | null | undefined): void {
    if (!icon) {
      return;
    }
    this._icons.set(icon.name, icon.data);
    this._version.update((v) => v + 1);
  }

  /** Register multiple icons at once. Nullish entries are skipped. */
  registerAll(icons: readonly (IrdIconDefinition | null | undefined)[]): void {
    let changed = false;
    for (const icon of icons) {
      if (!icon) {
        continue;
      }
      if (!this._icons.has(icon.name)) {
        changed = true;
      }
      this._icons.set(icon.name, icon.data);
    }
    // Only bump the version when at least one icon was actually new, so a
    // repeat `registerAll` of an already-registered set (e.g. one per
    // component instantiation) does not force every active icon to re-render.
    if (changed) {
      this._version.update((v) => v + 1);
    }
  }

  /** Get SVG data by name. Returns `undefined` if not registered. */
  get(name: string): string | undefined {
    return this._icons.get(name);
  }

  /** Check whether an icon name is registered. */
  has(name: string): boolean {
    return this._icons.has(name);
  }
}
